import React, { useEffect, useState } from 'react';
import ImageCard from './ImageCard';
import ImageModal from './ImageModal';
import { fetchImagesByTag } from '../services/api';
import './Gallery.css';

type ImageMeta = {
  filename: string;
  tags: string[];
  url?: string;
};

export interface GalleryProps {
  images?: ImageMeta[]; // optional prop from parent (Home)
  tag?: string; // optional external tag to filter by
}

const Gallery: React.FC<GalleryProps> = ({ images: externalImages, tag: externalTag }) => {
  // use externalImages if provided, otherwise manage local fetch/state
  const [images, setImages] = useState<ImageMeta[]>(externalImages ?? []);
  const [selectedImage, setSelectedImage] = useState<ImageMeta | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [localTag, setLocalTag] = useState('');
  const [page, setPage] = useState(0);
  // Random seed for consistent pagination within a session
  const [seed, setSeed] = useState(() => Math.floor(Math.random() * 1000000));
    const [limit] = useState(8); // ensure we use the same limit for fetch and UI
  // debug
  useEffect(() => { console.log('Gallery state', { page, limit, imagesLength: images.length, hasMore, loading, externalImages }); }, [page, images, hasMore, loading, externalImages]);
 
  // keep local state in sync when parent supplies images
  useEffect(() => {
    if (externalImages) {
      setImages(externalImages);
      setHasMore(false); // parent provided full/filtered set — pagination handled by parent if needed
    }
  }, [externalImages]);

    // when external tag changes, reset list and page so we fetch new results
  useEffect(() => {
    if (externalTag !== undefined) {
      setImages([]);
      setPage(0);
      setHasMore(true);
      setLocalTag(externalTag);
      // Generate new seed for fresh random order on new search
      setSeed(Math.floor(Math.random() * 1000000));
    }
  }, [externalTag]);


  // only fetch when parent did NOT provide images
  useEffect(() => {
      if (externalImages) return;

    const loadImages = async () => {
      setLoading(true);
      try {
        const effectiveTag = externalTag !== undefined ? externalTag : localTag;
        const response = await fetchImagesByTag(effectiveTag, page, /*limit*/ 12, seed);
        // On page 0, replace images; otherwise append
        if (page === 0) {
          setImages(response.images);
        } else {
          setImages(prev => {
            // Deduplicate by filename
            const existingFilenames = new Set(prev.map(img => img.filename));
            const newImages = response.images.filter((img: ImageMeta) => !existingFilenames.has(img.filename));
            return [...prev, ...newImages];
          });
        }
        // derive hasMore conservatively (optional)
        setHasMore(response.hasMore ?? (response.images?.length >= 12));
      } catch (err) {
        console.error('Failed to load images', err);
      } finally {
        setLoading(false);
      }
    };

    loadImages();
   }, [localTag, page, externalImages, externalTag, seed]);

  const handleSearch = (newTag: string) => {
    // keep local search behavior working when Gallery houses the SearchBar
    setLocalTag(newTag);
    setImages([]);
    setPage(0);
    setHasMore(true);
    // Generate new seed for fresh random order
    setSeed(Math.floor(Math.random() * 1000000));
  };

  const loadMoreImages = () => {
    console.log("loadMore clicked - loading:", loading);
     if (!loading) setPage(p => p + 1);
  };

  const handleImageClick = (image: ImageMeta) => setSelectedImage(image);
  const closeModal = () => setSelectedImage(null);

  return (
    <div>
      <div className="gallery">
        {images.map(image => (
          <ImageCard key={image.filename} image={image} onClick={() => handleImageClick(image)} />
        ))}
      </div>
      {!externalImages && (
        <div className="load-more-wrapper">
          <button className="load-more-btn" onClick={loadMoreImages} disabled={loading}>
            {loading ? 'Loading...' : 'Load More'}
          </button>
        </div>
      )}
      {selectedImage && <ImageModal image={selectedImage} onClose={closeModal} />}
    </div>
  );
 };

export default Gallery;
